import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Instagram, MessageCircle, CheckCircle2, Link2, Unlink, Presentation } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const fmtDate = (v) => { const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }); };

function Card({ icon: Icon, color, title, children }) {
  return (
    <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={22} color="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

const muted = { fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 };
const ok = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: 'var(--success)', marginBottom: 4 };

export default function Channels() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get('/channels').then(r => setData(r.data)).catch(() => toast.error('No se pudieron cargar los canales')), []);

  useEffect(() => {
    if (params.get('ig') === 'ok') { toast.success('Instagram conectado'); setParams({}, { replace: true }); }
    else if (params.get('ig_error')) { toast.error('Instagram: ' + params.get('ig_error'), { duration: 8000 }); setParams({}, { replace: true }); }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connectIg = async () => {
    setBusy(true);
    try { const r = await api.get('/channels/instagram/connect-url'); window.location.href = r.data.url; }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); setBusy(false); }
  };

  const disconnectIg = async () => {
    if (!window.confirm('¿Desconectar Instagram? El agente dejará de responder por ese canal.')) return;
    setBusy(true);
    try { await api.delete('/channels/instagram'); toast.success('Instagram desconectado'); await load(); }
    catch { toast.error('Error al desconectar'); }
    finally { setBusy(false); }
  };

  const setDemo = async (minutes) => {
    setBusy(true);
    try { await api.post('/channels/demo-mode', { minutes }); toast.success(minutes ? 'Modo demo activado por 1 hora' : 'Modo demo desactivado'); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setBusy(false); }
  };

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;
  const ig = data.instagram, wa = data.whatsapp;

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Canales</h1>
      <p style={{ ...muted, marginBottom: 24 }}>Conectá tus cuentas para que los mensajes lleguen a Conversaciones y el Agente IA pueda responder.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {data.demo_mode && (
          <Card icon={Presentation} color="#a855f7" title="Modo demo">
            {data.demo_mode.active ? (<>
              <div style={ok}><CheckCircle2 size={16} /> Activo hasta las {new Date(data.demo_mode.until).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</div>
              <div style={muted}>Todo lo que llegue al WhatsApp y al Instagram de uDream entra a este CRM y lo responde el agente de demo. Al terminar, vuelve solo a agencia.</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setDemo(60)} disabled={busy}>Extender 1 hora</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setDemo(0)} disabled={busy}>Desactivar ahora</button>
              </div>
            </>) : (<>
              <div style={muted}>Para mostrar en vivo cómo entra un mensaje: al activarlo, <b>durante 1 hora</b> todo lo que llegue al WhatsApp y al Instagram de uDream entra a este CRM demo (incluidos mensajes de clientes reales de agencia). Después vuelve solo a agencia.</div>
              <div style={{ marginTop: 12 }}><button className="btn btn-primary" onClick={() => setDemo(60)} disabled={busy}><Presentation size={16} /> Activar modo demo (1 hora)</button></div>
            </>)}
          </Card>
        )}
        <Card icon={Instagram} color="linear-gradient(45deg,#f09433,#dc2743,#bc1888)" title="Instagram">
          {ig.connected ? (<>
            <div style={ok}><CheckCircle2 size={16} /> Conectado{ig.username ? ' como @' + ig.username : ''}</div>
            {ig.expires_at && <div style={muted}>Acceso renovado automáticamente (vigente hasta {fmtDate(ig.expires_at)}).</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {ig.login_available && <button className="btn btn-secondary btn-sm" onClick={connectIg} disabled={busy}><Link2 size={14} /> Reconectar</button>}
              <button className="btn btn-ghost btn-sm" onClick={disconnectIg} disabled={busy}><Unlink size={14} /> Desconectar</button>
            </div>
          </>) : (<>
            <div style={muted}>Necesitás una cuenta de Instagram <b>profesional</b> (empresa o creador). Vas a iniciar sesión en Instagram y aceptar los permisos de mensajes.</div>
            <div style={{ marginTop: 12 }}>
              {ig.login_available
                ? <button className="btn btn-primary" onClick={connectIg} disabled={busy}><Instagram size={16} /> {busy ? 'Redirigiendo...' : 'Conectar Instagram'}</button>
                : <div style={muted}>La conexión con Instagram todavía no está disponible.</div>}
            </div>
          </>)}
        </Card>
        <Card icon={MessageCircle} color="#25D366" title="WhatsApp">
          {wa.connected
            ? <div style={ok}><CheckCircle2 size={16} /> Conectado: {wa.number}</div>
            : <div style={muted}>Para activar WhatsApp escribinos y lo configuramos con tu número.</div>}
        </Card>
      </div>
    </div>
  );
}
